import { createStore as createStoreRedux, combineReducers, type Store } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction'

import reducers from './reducers/index'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { DeepPartial, RootState } from 'types'
import { defaultState as navigationDefaultState } from './reducers/navigation'
import { reportError } from 'utils/error'
import { authCookie } from 'utils/cookies'
import workspaceSessionStorage from 'utils/workspaceSessionStorage'
import { NAVIGATION_STORAGE_KEY, OPEN_FOLDERS_STORAGE_KEY, SIDEBAR_WIDTH_KEY } from './constants'

const appReducer = combineReducers({ ...reducers })

const rootReducer = (state: any, action: any) => {
  // clean other branches of state tree on logout to prevent leakage of (possibly sensitive) info
  return appReducer(action.type === 'AUTH_UNSET' ? { auth: state.auth } : state, action)
}

const auth = authCookie || null
const headers = authCookie?.headers || {}

let navigation: RootState['navigation'] = navigationDefaultState
try {
  navigation = { ...navigation, ...JSON.parse(localStorage.getItem(NAVIGATION_STORAGE_KEY) || '{}') }
} catch (error) {
  reportError(error, { severity: 'error' })
}

const sessionWorkspaces = workspaceSessionStorage.getWorkspaces()
const currentWorkspaceId = auth?.last_workspace_id || auth?.company_id || ALMANAC_CORE_WORKSPACE_ID

const initialStore: Partial<RootState> = {
  auth,
  headers,
  // The Core ID is still required as a backup here, for unauthed users
  workspace: { ...sessionWorkspaces, currentWorkspaceId },
  navigation,
  suggestChangesModal: { state: null },
  suggestions: {},
  commandLineVisible: false,
  reviews: {},
  userArticleLikes: [],
  cardDismissals: [],
  wizardTourDismissals: [],
  educationPopovers: [],
  categories: { records: null },
  openFolderIds: JSON.parse(localStorage.getItem(OPEN_FOLDERS_STORAGE_KEY) || '[]'),
  sidebarWidth: JSON.parse(localStorage.getItem(SIDEBAR_WIDTH_KEY) || '256'),
  colors: {},
}

const store = createStoreRedux(rootReducer, initialStore, composeWithDevTools())

export default store

// Having a type gives us some auto-complete and fixes some typo errors
// We also need some guarantees on workspaces being at least an array when currentWorkspaceId matters
export type TestingRootState = DeepPartial<RootState> & {
  workspace?: { [P in keyof RootState['workspace']]: DeepPartial<RootState['workspace'][P]> }
}
// for testing
export function createStore(initialStore: TestingRootState = {}): Store<RootState> {
  return createStoreRedux(rootReducer, initialStore as any)
}
